import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import EmptyState from '../../../../components/EmptyState';
import PermissionGuard from '../../../../components/PermissionGuard';
import { useAuth } from '../../../../context/AuthContext';
import { Permissions } from '../../../../constants/Permissions';
import { customerService, CustomerDTO } from '../../../../services/customerService';
import '../../../../pages/Products/ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2, Users } from 'lucide-react';

const CustomerListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomers({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setCustomers(data);
      }
      setLoading(false);
    } catch {
      setError(t('customer.failedToLoadCustomer'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await customerService.getCustomers({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setCustomers(data);
      }
    } catch {
      setError(t('customer.failedToLoadCustomer'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'code',
      label: t('customer.customerCode'),
      sortable: true,
      width: '130px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'name',
      label: t('customer.customerName'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'contactEmail',
      label: t('customer.contactEmail'),
      sortable: true,
      width: '200px',
    },
    {
      key: 'contactPhone',
      label: t('customer.contactPhone'),
      sortable: false,
      width: '140px',
      hideTablet: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'customerType',
      label: t('customer.customerType'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'city',
      label: t('customer.city'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'country',
      label: t('customer.country'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'isActive',
      label: t('customer.status'),
      width: '100px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('customer.active') : t('customer.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item: CustomerDTO) => navigate(`/customers/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Customers.Edit)
      ? [{
          icon: (
            <SquarePen size={16} strokeWidth={1.75} />
          ),
          onClick: (item: CustomerDTO) => navigate(`/customers/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.Customers.Delete)
      ? [{
          icon: (
            <Trash2 size={16} strokeWidth={1.75} />
          ),
          onClick: (item: CustomerDTO) => navigate(`/customers/${item.id}/delete`),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('customer.title')}</h1>
          <div className="header-actions">
            <PermissionGuard claim={Permissions.Customers.Create}>
              <button
                className="btn-new"
                onClick={() => navigate('/customers/create')}
              >
                <CirclePlus/>
                {t('customer.newCustomer')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('customer.refreshCustomers')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('customer.refreshCustomers')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={customers}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('customer.noCustomersFound')}
          emptyState={(
            <EmptyState
              icon={<Users size={44} strokeWidth={1.2} />}
              title={t('emptyState.customers.title') || 'No customers yet'}
              description={t('emptyState.customers.text')
                || 'Customers let you track credit sales, outstanding balances and sales history per buyer. Walk-in sales work without one, but regulars are worth adding.'}
              actionLabel={t('customer.newCustomer') || 'New Customer'}
              onAction={() => navigate('/customers/create')}
            />
          )}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default CustomerListPage;
